import { useState, useEffect, useCallback, useMemo } from 'react'
import {
  Users, Plus, Trash2, Edit2, RefreshCw, ShieldCheck,
  Save, X, ChevronDown, ChevronUp, Check, Search,
  Clock, AlertTriangle, Ban, CheckCircle, Filter,
  FileDown, Table2, Eye, EyeOff, Globe,
  CalendarDays, Briefcase, HeartPulse, Baby,
  GraduationCap, BookOpen, LayoutGrid, BarChart3,
  ChevronLeft, ChevronRight
} from 'lucide-react'
import { useAuthStore } from '@/store/authStore'
import { apiService } from '@/services/api'
import { toast } from 'sonner'

// ============================================================
// I18N
// ============================================================
type Lang = 'fr' | 'en'

const i18n = {
  fr: {
    legalRef: 'Réf. : Loi n°2003-011 du 03/09/2003 — Art. 13 (ponctualité, assiduité, plein emploi)',
    legalRef2: 'BOEN n°9 — Cahier de pointage / Fiche individuelle de relevé',
    title: 'Assiduité des Enseignants',
    subtitle: 'Pointage par emploi du temps, retards, absences, permissions et congés',
    tabEdt: 'Par emploi du temps',
    tabLibre: 'Pointage libre',
    tabRapport: 'Rapport par classe',
    selectClass: 'Classe',
    selectDate: 'Date',
    today: "Aujourd'hui",
    prevDay: 'Jour précédent',
    nextDay: 'Jour suivant',
    prevWeek: 'Semaine précédente',
    nextWeek: 'Semaine suivante',
    weekOf: 'Semaine du',
    noEdt: 'Aucun emploi du temps pour cette classe',
    noSlots: 'Aucun créneau ce jour',
    slot: 'Créneau',
    subject: 'Matière',
    teacher: 'Enseignant',
    status: 'Statut',
    arrival: 'Arrivée',
    departure: 'Départ',
    delay: 'Retard (min)',
    reason: 'Motif',
    notes: 'Observations',
    date: 'Date',
    present: 'Présent',
    absent: 'Absent',
    late: 'Retard',
    permission: 'Permission',
    conge: 'Congé',
    loading: 'Chargement...',
    page: 'Page',
    previous: 'Précédent',
    next: 'Suivant',
    searchPlaceholder: 'Rechercher un enseignant...',
    noData: 'Aucune donnée',
    totalHours: 'Volume horaire annuel',
    hours900: '900h (MINESEC)',
    statsTitle: 'Statistiques',
    presenceRate: 'Taux de présence',
    absences: 'Absences',
    delays: 'Retards',
    alerts: 'Alertes',
    alertExcessAbsences: 'Absences excessives (≥3)',
    alertExcessDelays: 'Retards excessifs (≥60 min)',
    save: 'Enregistrer',
    saving: 'Enregistrement...',
    cancel: 'Annuler',
    deleteConfirm: 'Supprimer cette entrée ?',
    errorLoad: 'Erreur chargement',
    errorSave: 'Erreur sauvegarde',
    errorDelete: 'Erreur suppression',
    entryCreated: 'Pointage enregistré',
    entryUpdated: 'Pointage mis à jour',
    entryDeleted: 'Pointage supprimé',
    filteredView: 'Vue filtrée',
    fullView: 'Vue complète',
    allTeachers: 'Tous les enseignants',
    allStatuses: 'Tous les statuts',
    fromDate: 'Du',
    toDate: 'Au',
    filter: 'Filtrer',
    reset: 'Réinitialiser',
    conformite: 'Conformité MINESEC',
    newEntry: 'Nouveau pointage',
    quickPointage: 'Pointage rapide',
    pointageDone: 'Pointage effectué',
    pointageMissing: 'Pointage manquant',
    rapportTitle: "Rapport d'assiduité",
    rapportSubtitle: 'Par enseignant et par période',
    rapportPeriod: 'Période',
    rapportGenerate: 'Générer le rapport',
    rapportTeacher: 'Enseignant',
    rapportPresent: 'Présent',
    rapportAbsent: 'Absent',
    rapportLate: 'Retard',
    rapportPermission: 'Permission',
    rapportConge: 'Congé',
    rapportDelayTotal: 'Min. retard',
    rapportNoData: 'Aucune donnée pour cette période',
    downloadPDF: 'PDF',
    downloadExcel: 'Excel',
    monday: 'Lundi',
    tuesday: 'Mardi',
    wednesday: 'Mercredi',
    thursday: 'Jeudi',
    friday: 'Vendredi',
    saturday: 'Samedi',
    sunday: 'Dimanche',
  },
  en: {
    legalRef: 'Ref: Law n°2003-011 of 03/09/2003 — Art. 13 (punctuality, attendance, full employment)',
    legalRef2: 'BOEN n°9 — Attendance register / Individual record sheet',
    title: 'Teacher Attendance',
    subtitle: 'Attendance by timetable, delays, absences, permissions and leave',
    tabEdt: 'By timetable',
    tabLibre: 'Free entry',
    tabRapport: 'Class report',
    selectClass: 'Class',
    selectDate: 'Date',
    today: 'Today',
    prevDay: 'Previous day',
    nextDay: 'Next day',
    prevWeek: 'Previous week',
    nextWeek: 'Next week',
    weekOf: 'Week of',
    noEdt: 'No timetable for this class',
    noSlots: 'No slots this day',
    slot: 'Slot',
    subject: 'Subject',
    teacher: 'Teacher',
    status: 'Status',
    arrival: 'Arrival',
    departure: 'Departure',
    delay: 'Delay (min)',
    reason: 'Reason',
    notes: 'Notes',
    date: 'Date',
    present: 'Present',
    absent: 'Absent',
    late: 'Late',
    permission: 'Permission',
    conge: 'Leave',
    loading: 'Loading...',
    page: 'Page',
    previous: 'Previous',
    next: 'Next',
    searchPlaceholder: 'Search for a teacher...',
    noData: 'No data',
    totalHours: 'Annual workload',
    hours900: '900h (MINESEC)',
    statsTitle: 'Statistics',
    presenceRate: 'Presence rate',
    absences: 'Absences',
    delays: 'Delays',
    alerts: 'Alerts',
    alertExcessAbsences: 'Excessive absences (≥3)',
    alertExcessDelays: 'Excessive delays (≥60 min)',
    save: 'Save',
    saving: 'Saving...',
    cancel: 'Cancel',
    deleteConfirm: 'Delete this entry?',
    errorLoad: 'Loading error',
    errorSave: 'Save error',
    errorDelete: 'Delete error',
    entryCreated: 'Attendance recorded',
    entryUpdated: 'Attendance updated',
    entryDeleted: 'Attendance deleted',
    filteredView: 'Filtered view',
    fullView: 'Full view',
    allTeachers: 'All teachers',
    allStatuses: 'All statuses',
    fromDate: 'From',
    toDate: 'To',
    filter: 'Filter',
    reset: 'Reset',
    conformite: 'MINESEC Compliance',
    newEntry: 'New entry',
    quickPointage: 'Quick attendance',
    pointageDone: 'Attendance done',
    pointageMissing: 'Attendance missing',
    rapportTitle: 'Attendance report',
    rapportSubtitle: 'By teacher and period',
    rapportPeriod: 'Period',
    rapportGenerate: 'Generate report',
    rapportTeacher: 'Teacher',
    rapportPresent: 'Present',
    rapportAbsent: 'Absent',
    rapportLate: 'Late',
    rapportPermission: 'Permission',
    rapportConge: 'Leave',
    rapportDelayTotal: 'Delay min.',
    rapportNoData: 'No data for this period',
    downloadPDF: 'PDF',
    downloadExcel: 'Excel',
    monday: 'Monday',
    tuesday: 'Tuesday',
    wednesday: 'Wednesday',
    thursday: 'Thursday',
    friday: 'Friday',
    saturday: 'Saturday',
    sunday: 'Sunday',
  }
}

function useTranslation() {
  const [lang, setLang] = useState<Lang>(() => {
    const stored = localStorage.getItem('app_language') as Lang
    return stored === 'en' ? 'en' : 'fr'
  })
  const t = useMemo(() => i18n[lang], [lang])
  const toggleLang = useCallback(() => {
    const newLang: Lang = lang === 'fr' ? 'en' : 'fr'
    setLang(newLang)
    localStorage.setItem('app_language', newLang)
    window.dispatchEvent(new CustomEvent('languageChanged', { detail: newLang }))
  }, [lang])
  useEffect(() => {
    const handler = (e: Event) => {
      const ce = e as CustomEvent<Lang>
      if (ce.detail && (ce.detail === 'fr' || ce.detail === 'en')) setLang(ce.detail)
    }
    window.addEventListener('languageChanged', handler)
    return () => window.removeEventListener('languageChanged', handler)
  }, [])
  return { t, lang, toggleLang }
}

const DAY_NAMES_FR = ['', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche']
const DAY_NAMES_EN = ['', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

interface Teacher {
  id: number
  first_name: string
  last_name: string
  email?: string
  phone?: string
  matricule?: string
  specialty?: string
}

interface ClassItem {
  id: number
  name: string
}

interface TimetableSlot {
  id: number
  day_of_week: number
  start_time: string
  end_time: string
  subject_id: number
  subject_name?: string
  teacher_id: number
  teacher_name?: string
  room?: string
}

interface AttendanceRecord {
  id: number
  teacher_id: number
  attendance_date: string
  status: 'present' | 'absent' | 'late' | 'permission' | 'conge'
  arrival_time?: string
  departure_time?: string
  delay_minutes?: number
  reason?: string
  notes?: string
  timetable_slot_id?: number
  subject_name?: string
  slot_start_time?: string
  slot_end_time?: string
  teacher_name?: string
  teacher_matricule?: string
  recorded_by_name?: string
  created_at?: string
}

interface RapportRow {
  teacher_id: number
  teacher_name: string
  teacher_matricule?: string
  total_records: number
  present_count: number
  absent_count: number
  late_count: number
  permission_count: number
  conge_count: number
  total_delay_minutes: number
}

const STATUS_CONFIG = [
  { value: 'present', label: { fr: 'Présent', en: 'Present' }, color: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle },
  { value: 'absent', label: { fr: 'Absent', en: 'Absent' }, color: 'bg-red-100 text-red-700 border-red-200', icon: Ban },
  { value: 'late', label: { fr: 'Retard', en: 'Late' }, color: 'bg-amber-100 text-amber-700 border-amber-200', icon: Clock },
  { value: 'permission', label: { fr: 'Permission', en: 'Permission' }, color: 'bg-blue-100 text-blue-700 border-blue-200', icon: Briefcase },
  { value: 'conge', label: { fr: 'Congé', en: 'Leave' }, color: 'bg-purple-100 text-purple-700 border-purple-200', icon: CalendarDays },
]

export default function TeacherDisciplinePage() {
  const { t, lang, toggleLang } = useTranslation()
  const { user, role } = useAuthStore()

  const hasFullAccess = ['super_admin', 'admin', 'proviseur', 'censeur'].includes(role || '')
  const canWrite = hasFullAccess || ((user as any)?.permissions || []).includes('teacher_discipline_write')

  // ─── TABS ───
  const [activeTab, setActiveTab] = useState<'edt' | 'libre' | 'rapport'>('edt')

  // ─── CLASSE & DATE ───
  const [classes, setClasses] = useState<ClassItem[]>([])
  const [selectedClass, setSelectedClass] = useState('')
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0])

  // ─── EDT ───
  const [timetableSlots, setTimetableSlots] = useState<TimetableSlot[]>([])
  const [edtLoading, setEdtLoading] = useState(false)

  // ─── POINTAGES ───
  const [attendanceRecords, setAttendanceRecords] = useState<AttendanceRecord[]>([])
  const [recordsLoading, setRecordsLoading] = useState(false)

  // ─── RAPPORT ───
  const [rapportData, setRapportData] = useState<RapportRow[]>([])
  const [rapportDateFrom, setRapportDateFrom] = useState('')
  const [rapportDateTo, setRapportDateTo] = useState('')
  const [rapportLoading, setRapportLoading] = useState(false)
  // ── Rapport PAR ENSEIGNANT (toutes classes — classe non requise) ──
  const [reportTeacherId, setReportTeacherId] = useState('')
  const [teacherRapport, setTeacherRapport] = useState<any>(null)

  // ─── LIBRE ───
  const [teachers, setTeachers] = useState<Teacher[]>([])
  const [records, setRecords] = useState<AttendanceRecord[]>([])
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [showModal, setShowModal] = useState(false)
  const [editingRecord, setEditingRecord] = useState<AttendanceRecord | null>(null)
  const [formData, setFormData] = useState<Partial<AttendanceRecord>>({
    teacher_id: undefined, attendance_date: new Date().toISOString().split('T')[0],
    status: 'present', arrival_time: '', departure_time: '', delay_minutes: 0,
    reason: '', notes: '',
  })
  const [filterStatus, setFilterStatus] = useState('')
  const [filterDateFrom, setFilterDateFrom] = useState('')
  const [filterDateTo, setFilterDateTo] = useState('')
  const [showFilters, setShowFilters] = useState(false)

  const currentUserSchoolId = useMemo(() => {
    const raw = (user as any)?.school_id
    const parsed = typeof raw === 'string' ? parseInt(raw, 10) : raw
    return (typeof parsed === 'number' && !isNaN(parsed)) ? parsed : undefined
  }, [user])

  // ─── LOAD CLASSES ───
  const loadClasses = useCallback(async () => {
    try {
      const res: any = await apiService.get('/classes', {
        params: { school_id: currentUserSchoolId, per_page: 1000 }
      })
      const data = Array.isArray(res) ? res : (res.data?.items || res.data || res.items || [])
      setClasses(data)
    } catch (err) { console.error(err) }
  }, [currentUserSchoolId])

  // ─── LOAD EDT ───
  const loadEdt = useCallback(async () => {
    if (!selectedClass) { setTimetableSlots([]); return }
    setEdtLoading(true)
    try {
      const res: any = await apiService.get('/timetable/slots', {
        params: { class_id: parseInt(selectedClass), school_id: currentUserSchoolId }
      })
      const data = Array.isArray(res) ? res : (res.data || [])
      setTimetableSlots(data)
    } catch (err) { console.error('Error loading EDT:', err) }
    finally { setEdtLoading(false) }
  }, [selectedClass, currentUserSchoolId])

  // ─── LOAD ATTENDANCE BY CLASS/DATE ───
  const loadAttendanceByClass = useCallback(async () => {
    if (!selectedClass || !selectedDate) { setAttendanceRecords([]); return }
    setRecordsLoading(true)
    try {
      const res: any = await apiService.get(`/discipline/teacher-attendance/by-class/${selectedClass}`, {
        params: { date: selectedDate }
      })
      const data = Array.isArray(res) ? res : (res.data || [])
      setAttendanceRecords(data)
    } catch (err) { console.error('Error loading attendance:', err) }
    finally { setRecordsLoading(false) }
  }, [selectedClass, selectedDate])

  // ─── LOAD RAPPORT ───
  const loadRapport = useCallback(async () => {
    if (!selectedClass) { setRapportData([]); return }
    setRapportLoading(true)
    try {
      const params: any = {}
      if (rapportDateFrom) params.date_from = rapportDateFrom
      if (rapportDateTo) params.date_to = rapportDateTo
      const res: any = await apiService.get(`/discipline/teacher-attendance/report/${selectedClass}`, { params })
      setRapportData(res?.teachers || [])
    } catch (err) { console.error('Error loading rapport:', err) }
    finally { setRapportLoading(false) }
  }, [selectedClass, rapportDateFrom, rapportDateTo])

  // ─── LOAD RAPPORT PAR ENSEIGNANT (toutes classes) ───
  const loadTeacherRapport = useCallback(async () => {
    if (!reportTeacherId) { setTeacherRapport(null); return }
    setRapportLoading(true)
    try {
      const params: any = {}
      if (rapportDateFrom) params.date_from = rapportDateFrom
      if (rapportDateTo) params.date_to = rapportDateTo
      const res: any = await apiService.get(`/discipline/teacher-attendance/report/teacher/${reportTeacherId}`, { params })
      setTeacherRapport(res)
    } catch (err) { console.error('Error loading teacher rapport:', err); toast.error(t.errorLoad) }
    finally { setRapportLoading(false) }
  }, [reportTeacherId, rapportDateFrom, rapportDateTo, t])

  // ─── TÉLÉCHARGEMENTS (axios blob — cookies httpOnly) ───
  const downloadBlob = (res: any, filename: string) => {
    const blob = res instanceof Blob ? res : new Blob([res])
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  }

  const handleDownloadTeacherReport = async (format: 'pdf' | 'excel') => {
    if (!reportTeacherId) { toast.error(lang === 'fr' ? 'Choisissez un enseignant' : 'Choose a teacher'); return }
    try {
      const params: any = { format, teacher_id: reportTeacherId }
      if (rapportDateFrom) params.date_from = rapportDateFrom
      if (rapportDateTo) params.date_to = rapportDateTo
      const res: any = await apiService.get('/discipline/teacher-attendance/download/report', { params, responseType: 'blob' as any })
      downloadBlob(res, `assiduite_enseignant_${new Date().toISOString().split('T')[0]}.${format === 'pdf' ? 'pdf' : 'xlsx'}`)
      toast.success(lang === 'fr' ? 'Téléchargement terminé' : 'Download complete')
    } catch (err) { console.error(err); toast.error(lang === 'fr' ? 'Erreur de téléchargement' : 'Download error') }
  }

  const handleDownloadClassReport = async (format: 'pdf' | 'excel') => {
    if (!selectedClass) { toast.error(lang === 'fr' ? 'Choisissez une classe' : 'Choose a class'); return }
    try {
      const params: any = { format, class_id: selectedClass }
      if (rapportDateFrom) params.date_from = rapportDateFrom
      if (rapportDateTo) params.date_to = rapportDateTo
      const res: any = await apiService.get('/discipline/teacher-attendance/download/report', { params, responseType: 'blob' as any })
      downloadBlob(res, `assiduite_classe_${new Date().toISOString().split('T')[0]}.${format === 'pdf' ? 'pdf' : 'xlsx'}`)
      toast.success(lang === 'fr' ? 'Téléchargement terminé' : 'Download complete')
    } catch (err) { console.error(err); toast.error(lang === 'fr' ? 'Erreur de téléchargement' : 'Download error') }
  }

  // ─── LOAD TEACHERS (libre) ───
  const loadTeachers = useCallback(async () => {
    try {
      const res: any = await apiService.get('/teachers', {
        params: { school_id: currentUserSchoolId, per_page: 1000, status: 'active' }
      })
      const data = Array.isArray(res) ? res : (res.data?.items || res.data || res.items || [])
      setTeachers(data)
    } catch (err) { console.error(err) }
  }, [currentUserSchoolId])

  // ─── LOAD RECORDS (libre) ───
  const loadRecords = useCallback(async () => {
    setLoading(true)
    try {
      const params: any = { page, per_page: 20, school_id: currentUserSchoolId }
      if (search) params.search = search
      if (filterStatus) params.status = filterStatus
      if (filterDateFrom) params.date_from = filterDateFrom
      if (filterDateTo) params.date_to = filterDateTo
      const res: any = await apiService.get('/discipline/teacher-attendance', { params })
      const data = Array.isArray(res) ? res : (res.data?.items || res.data || res.items || [])
      setRecords(data)
    } catch (err) { console.error(err); toast.error(t.errorLoad) }
    finally { setLoading(false) }
  }, [page, search, filterStatus, filterDateFrom, filterDateTo, currentUserSchoolId, t])

  useEffect(() => { loadClasses(); loadTeachers() }, [loadClasses, loadTeachers])
  useEffect(() => { loadEdt(); loadAttendanceByClass() }, [loadEdt, loadAttendanceByClass])
  useEffect(() => { if (activeTab === 'rapport') loadRapport() }, [activeTab, loadRapport])
  useEffect(() => {
    if (activeTab === 'rapport' && reportTeacherId) loadTeacherRapport()
    else setTeacherRapport(null)
  }, [activeTab, reportTeacherId, loadTeacherRapport])
  useEffect(() => { if (activeTab === 'libre') loadRecords() }, [activeTab, loadRecords])

  // ─── QUICK POINTAGE (par créneau EDT) ───
  const handleQuickPointage = async (slot: TimetableSlot, status: string) => {
    if (!canWrite) return
    try {
      await apiService.post('/discipline/teacher-attendance', {
        teacher_id: slot.teacher_id,
        date: selectedDate,
        status,
        arrival_time: slot.start_time?.slice(0, 5),
        departure_time: slot.end_time?.slice(0, 5),
        delay_minutes: status === 'late' ? 15 : 0,
        reason: status === 'absent' ? 'Absent au créneau EDT' : status === 'late' ? 'Retard au créneau' : '',
        notes: `Pointage via EDT — ${slot.subject_name || 'Matière'}`,
        school_id: currentUserSchoolId,
        timetable_slot_id: slot.id,
      })
      toast.success(t.entryCreated)
      loadAttendanceByClass()
    } catch (err) { toast.error(t.errorSave) }
  }

  // ─── LIBRE: SAVE ───
  const handleSave = async () => {
    if (!formData.teacher_id || !formData.attendance_date) {
      toast.error(lang === 'fr' ? 'Enseignant et date requis' : 'Teacher and date required')
      return
    }
    try {
      const payload = { ...formData, school_id: currentUserSchoolId }
      if (editingRecord) {
        await apiService.put(`/discipline/teacher-attendance/${editingRecord.id}`, payload)
        toast.success(t.entryUpdated)
      } else {
        await apiService.post('/discipline/teacher-attendance', payload)
        toast.success(t.entryCreated)
      }
      setShowModal(false)
      setEditingRecord(null)
      loadRecords()
      loadAttendanceByClass()
    } catch (err) { toast.error(t.errorSave) }
  }

  const handleDelete = async (id: number) => {
    if (!confirm(t.deleteConfirm)) return
    try {
      await apiService.delete(`/discipline/teacher-attendance/${id}`)
      toast.success(t.entryDeleted)
      loadRecords()
      loadAttendanceByClass()
    } catch (err) { toast.error(t.errorDelete) }
  }

  const openEdit = (record: AttendanceRecord) => {
    if (!canWrite) return
    setEditingRecord(record)
    setFormData({
      teacher_id: record.teacher_id,
      attendance_date: record.attendance_date,
      status: record.status,
      arrival_time: record.arrival_time || '',
      departure_time: record.departure_time || '',
      delay_minutes: record.delay_minutes || 0,
      reason: record.reason || '',
      notes: record.notes || '',
    })
    setShowModal(true)
  }

  const openCreate = () => {
    if (!canWrite) return
    setEditingRecord(null)
    setFormData({
      teacher_id: undefined,
      attendance_date: new Date().toISOString().split('T')[0],
      status: 'present',
      arrival_time: '', departure_time: '', delay_minutes: 0,
      reason: '', notes: '',
    })
    setShowModal(true)
  }

  // ─── HELPERS ───
  const getStatusBadge = (status: string) => {
    const cfg = STATUS_CONFIG.find(s => s.value === status)
    if (!cfg) return <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-700">{status}</span>
    const Icon = cfg.icon
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${cfg.color}`}>
        <Icon className="w-3 h-3" />{cfg.label[lang]}
      </span>
    )
  }

  const getDayName = (dow: number) => lang === 'fr' ? DAY_NAMES_FR[dow] : DAY_NAMES_EN[dow]

  const getSelectedDateDay = () => {
    const d = new Date(selectedDate)
    return d.getDay() === 0 ? 7 : d.getDay()
  }

  // Slots du jour sélectionné
  const daySlots = useMemo(() => {
    const dow = getSelectedDateDay()
    return timetableSlots.filter(s => s.day_of_week === dow).sort((a, b) => a.start_time.localeCompare(b.start_time))
  }, [timetableSlots, selectedDate])

  // Pointage existant pour un créneau
  const getAttendanceForSlot = (slotId: number) => attendanceRecords.find(r => r.timetable_slot_id === slotId)

  // Stats libre
  const stats = useMemo(() => {
    const total = records.length
    const present = records.filter(r => r.status === 'present').length
    const absent = records.filter(r => r.status === 'absent').length
    const late = records.filter(r => r.status === 'late').length
    const presenceRate = total > 0 ? Math.round((present / total) * 100) : 0
    return { total, present, absent, late, presenceRate }
  }, [records])

  // Navigation date
  const addDays = (dateStr: string, days: number) => {
    const d = new Date(dateStr)
    d.setDate(d.getDate() + days)
    return d.toISOString().split('T')[0]
  }

  return (
    <div className="animate-fade-in space-y-6">
      {/* Header */}
      <div className="page-header">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="page-title flex items-center gap-3">
              <Users className="h-7 w-7 text-primary-600" />
              {t.title}
            </h1>
            <p className="page-subtitle">{t.subtitle}</p>
            <p className="text-xs text-gray-500 mt-1">{t.legalRef}</p>
            <p className="text-xs text-gray-400">{t.legalRef2}</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <button onClick={toggleLang} className="flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors shadow-sm">
              <Globe className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">{lang === 'fr' ? 'FR' : 'EN'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Barre de contrôle: Classe + Date + Onglets */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-4 shadow-sm">
        <div className="flex flex-col lg:flex-row items-start lg:items-center gap-4">
          {/* Sélecteur classe */}
          <div className="flex-1 min-w-[200px]">
            <label className="label text-xs text-gray-500 mb-1">{t.selectClass}</label>
            <select value={selectedClass} onChange={(e) => setSelectedClass(e.target.value)} className="input w-full">
              <option value="">{lang === 'fr' ? 'Sélectionner une classe...' : 'Select a class...'}</option>
              {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>

          {/* Sélecteur date + navigation */}
          <div className="flex-1 min-w-[200px]">
            <label className="label text-xs text-gray-500 mb-1">{t.selectDate}</label>
            <div className="flex items-center gap-2">
              <button onClick={() => setSelectedDate(addDays(selectedDate, -7))} className="p-1.5 hover:bg-gray-100 rounded-lg" title={t.prevWeek}>
                <ChevronLeft className="w-4 h-4 text-gray-500" />
              </button>
              <button onClick={() => setSelectedDate(addDays(selectedDate, -1))} className="p-1.5 hover:bg-gray-100 rounded-lg" title={t.prevDay}>
                <ChevronLeft className="w-4 h-4 text-gray-500" />
              </button>
              <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} className="input text-center min-w-[140px]" />
              <button onClick={() => setSelectedDate(addDays(selectedDate, 1))} className="p-1.5 hover:bg-gray-100 rounded-lg" title={t.nextDay}>
                <ChevronRight className="w-4 h-4 text-gray-500" />
              </button>
              <button onClick={() => setSelectedDate(addDays(selectedDate, 7))} className="p-1.5 hover:bg-gray-100 rounded-lg" title={t.nextWeek}>
                <ChevronRight className="w-4 h-4 text-gray-500" />
              </button>
              <button onClick={() => setSelectedDate(new Date().toISOString().split('T')[0])} className="px-2 py-1 text-xs bg-primary-50 text-primary-700 rounded-lg hover:bg-primary-100">
                {t.today}
              </button>
            </div>
          </div>

          {/* Onglets */}
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
            <button onClick={() => setActiveTab('edt')} className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${activeTab === 'edt' ? 'bg-white text-primary-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}>
              <LayoutGrid className="w-4 h-4" />{t.tabEdt}
            </button>
            <button onClick={() => setActiveTab('libre')} className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${activeTab === 'libre' ? 'bg-white text-primary-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}>
              <BookOpen className="w-4 h-4" />{t.tabLibre}
            </button>
            <button onClick={() => setActiveTab('rapport')} className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${activeTab === 'rapport' ? 'bg-white text-primary-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}>
              <BarChart3 className="w-4 h-4" />{t.tabRapport}
            </button>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════
          ONGLET 1: PAR EMPLOI DU TEMPS
          ═══════════════════════════════════════════════════════════════ */}
      {activeTab === 'edt' && (
        <div className="space-y-4">
          {!selectedClass ? (
            <div className="card p-12 text-center text-gray-400">
              <GraduationCap className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="text-sm">{lang === 'fr' ? 'Veuillez sélectionner une classe pour voir son emploi du temps' : 'Please select a class to see its timetable'}</p>
            </div>
          ) : edtLoading ? (
            <div className="card p-12 text-center text-gray-400">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2" />
              {t.loading}
            </div>
          ) : timetableSlots.length === 0 ? (
            <div className="card p-12 text-center text-gray-400">
              <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="text-sm">{t.noEdt}</p>
            </div>
          ) : (
            <>
              {/* Info jour */}
              <div className="flex items-center gap-3">
                <CalendarDays className="w-5 h-5 text-primary-600" />
                <h3 className="text-lg font-semibold text-gray-900">
                  {getDayName(getSelectedDateDay())} — {new Date(selectedDate).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-US', { day: 'numeric', month: 'long', year: 'numeric' })}
                </h3>
                <span className="px-2 py-0.5 bg-primary-100 text-primary-700 rounded text-xs font-medium">
                  {daySlots.length} {lang === 'fr' ? 'créneau(x)' : 'slot(s)'}
                </span>
              </div>

              {daySlots.length === 0 ? (
                <div className="card p-8 text-center text-gray-400">
                  <Clock className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p>{t.noSlots}</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-3">
                  {daySlots.map((slot) => {
                    const existing = getAttendanceForSlot(slot.id)
                    return (
                      <div key={slot.id} className={`card p-4 border-l-4 transition-all hover:shadow-md ${existing ? 'border-l-green-500 bg-green-50/30' : 'border-l-gray-300'}`}>
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                          <div className="flex items-center gap-4">
                            <div className="w-14 h-14 bg-primary-100 rounded-xl flex flex-col items-center justify-center shrink-0">
                              <span className="text-xs font-bold text-primary-700">{slot.start_time?.slice(0, 5)}</span>
                              <span className="text-[10px] text-primary-500">{slot.end_time?.slice(0, 5)}</span>
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900">{slot.subject_name || '—'}</p>
                              <p className="text-sm text-gray-600 flex items-center gap-1">
                                <Users className="w-3 h-3" />
                                {slot.teacher_name || '—'}
                              </p>
                              <p className="text-xs text-gray-400">{lang === 'fr' ? 'Salle' : 'Room'}: {slot.room || '—'}</p>
                            </div>
                          </div>

                          <div className="flex items-center gap-3">
                            {existing ? (
                              <div className="flex items-center gap-2">
                                {getStatusBadge(existing.status)}
                                <span className="text-xs text-gray-500">
                                  {existing.delay_minutes ? `+${existing.delay_minutes}min` : ''}
                                </span>
                                {canWrite && (
                                  <button
                                    onClick={() => {
                                      // Modifier le pointage existant
                                      setEditingRecord(existing)
                                      setFormData({
                                        teacher_id: existing.teacher_id,
                                        attendance_date: existing.attendance_date,
                                        status: existing.status,
                                        arrival_time: existing.arrival_time || '',
                                        departure_time: existing.departure_time || '',
                                        delay_minutes: existing.delay_minutes || 0,
                                        reason: existing.reason || '',
                                        notes: existing.notes || '',
                                      })
                                      setShowModal(true)
                                    }}
                                    className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-500"
                                  >
                                    <Edit2 className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            ) : canWrite ? (
                              <div className="flex items-center gap-1">
                                <span className="text-xs text-gray-400 mr-2">{t.quickPointage}:</span>
                                {STATUS_CONFIG.map((cfg) => (
                                  <button
                                    key={cfg.value}
                                    onClick={() => handleQuickPointage(slot, cfg.value)}
                                    className={`px-2 py-1 rounded-lg text-xs font-medium border transition-all hover:shadow-sm ${cfg.color}`}
                                    title={cfg.label[lang]}
                                  >
                                    {cfg.label[lang]}
                                  </button>
                                ))}
                              </div>
                            ) : (
                              <span className="text-xs text-gray-400">{t.pointageMissing}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════
          ONGLET 2: POINTAGE LIBRE
          ═══════════════════════════════════════════════════════════════ */}
      {activeTab === 'libre' && (
        <div className="space-y-6">
          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="card p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">{t.totalHours}</p>
                  <p className="text-xl font-bold text-gray-900">{t.hours900}</p>
                </div>
                <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center"><Clock className="w-5 h-5 text-primary-600" /></div>
              </div>
            </div>
            <div className="card p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">{t.presenceRate}</p>
                  <p className="text-xl font-bold text-green-600">{stats.presenceRate}%</p>
                </div>
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center"><CheckCircle className="w-5 h-5 text-green-600" /></div>
              </div>
            </div>
            <div className="card p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">{t.absences}</p>
                  <p className="text-xl font-bold text-red-600">{stats.absent}</p>
                </div>
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center"><Ban className="w-5 h-5 text-red-600" /></div>
              </div>
            </div>
            <div className="card p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">{t.delays}</p>
                  <p className="text-xl font-bold text-amber-600">{stats.late}</p>
                </div>
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center"><Clock className="w-5 h-5 text-amber-600" /></div>
              </div>
            </div>
          </div>

          {/* Controls */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="relative w-full sm:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input type="text" placeholder={t.searchPlaceholder} value={search} onChange={(e) => setSearch(e.target.value)} className="input pl-10 w-full" />
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setShowFilters(!showFilters)} className={`btn-outline whitespace-nowrap flex items-center gap-2 ${showFilters ? 'bg-primary-50 border-primary-300 text-primary-700' : ''}`}>
                <Filter className="w-4 h-4" />{lang === 'fr' ? 'Filtres' : 'Filters'}
                {showFilters ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              </button>
              {canWrite && <button onClick={openCreate} className="btn-primary whitespace-nowrap"><Plus className="mr-2 h-4 w-4" />{t.newEntry}</button>}
            </div>
          </div>

          {showFilters && (
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-3">
              <div className="flex flex-wrap items-end gap-3">
                <div className="flex-1 min-w-[160px]">
                  <label className="label text-xs text-gray-500 mb-1">{t.status}</label>
                  <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="input w-full">
                    <option value="">{t.allStatuses}</option>
                    {STATUS_CONFIG.map(s => <option key={s.value} value={s.value}>{s.label[lang]}</option>)}
                  </select>
                </div>
                <div className="flex-1 min-w-[160px]">
                  <label className="label text-xs text-gray-500 mb-1">{t.fromDate}</label>
                  <input type="date" value={filterDateFrom} onChange={(e) => setFilterDateFrom(e.target.value)} className="input w-full" />
                </div>
                <div className="flex-1 min-w-[160px]">
                  <label className="label text-xs text-gray-500 mb-1">{t.toDate}</label>
                  <input type="date" value={filterDateTo} onChange={(e) => setFilterDateTo(e.target.value)} className="input w-full" />
                </div>
                <button onClick={() => { setFilterStatus(''); setFilterDateFrom(''); setFilterDateTo(''); setSearch(''); }} className="btn-outline text-sm whitespace-nowrap">
                  <X className="w-3 h-3 mr-1" />{t.reset}
                </button>
              </div>
            </div>
          )}

          {/* Table */}
          <div className="card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50/50">
                    <th className="text-left py-3 px-4 font-medium text-gray-600">{t.teacher}</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">{t.date}</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">{t.status}</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">{t.arrival}</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">{t.departure}</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">{t.delay}</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-600">{t.reason}</th>
                    <th className="text-right py-3 px-4 font-medium text-gray-600">{lang === 'fr' ? 'Actions' : 'Actions'}</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={8} className="py-12 text-center text-gray-400"><RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />{t.loading}</td></tr>
                  ) : records.length === 0 ? (
                    <tr><td colSpan={8} className="py-12 text-center text-gray-400"><Users className="w-8 h-8 mx-auto mb-2 opacity-30" />{t.noData}</td></tr>
                  ) : (
                    records.map((record) => {
                      const teacher = teachers.find(t => t.id === record.teacher_id)
                      return (
                        <tr key={record.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center text-xs font-bold text-primary-700">
                                {teacher?.first_name?.[0]}{teacher?.last_name?.[0]}
                              </div>
                              <div>
                                <p className="font-medium text-gray-900">{teacher?.first_name} {teacher?.last_name}</p>
                                <p className="text-xs text-gray-500">{teacher?.matricule || '-'}</p>
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-gray-600">{record.attendance_date}</td>
                          <td className="py-3 px-4">{getStatusBadge(record.status)}</td>
                          <td className="py-3 px-4 text-gray-600">{record.arrival_time || '-'}</td>
                          <td className="py-3 px-4 text-gray-600">{record.departure_time || '-'}</td>
                          <td className="py-3 px-4 text-gray-600">{record.delay_minutes ? `${record.delay_minutes} min` : '-'}</td>
                          <td className="py-3 px-4 text-gray-600 max-w-[200px] truncate">{record.reason || '-'}</td>
                          <td className="py-3 px-4 text-right">
                            <div className="flex items-center justify-end gap-1">
                              {canWrite && <button onClick={() => openEdit(record)} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-500"><Edit2 className="w-4 h-4" /></button>}
                              {canWrite && <button onClick={() => handleDelete(record.id)} className="p-1.5 hover:bg-red-50 rounded-lg text-gray-500 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>}
                            </div>
                          </td>
                        </tr>
                      )
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════
          ONGLET 3: RAPPORT PAR CLASSE
          ═══════════════════════════════════════════════════════════════ */}
      {activeTab === 'rapport' && (
        <div className="space-y-6">
          <div className="card p-6">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-2">
              <BarChart3 className="w-5 h-5 text-primary-600" />
              {t.rapportTitle}
            </h3>
            <p className="text-sm text-gray-500 mb-4">{t.rapportSubtitle}</p>

            {/* Contrôles : période + enseignant + génération */}
            <div className="flex flex-wrap items-end gap-3 mb-4">
              <div className="flex-1 min-w-[150px]">
                <label className="label text-xs text-gray-500 mb-1">{t.fromDate}</label>
                <input type="date" value={rapportDateFrom} onChange={(e) => setRapportDateFrom(e.target.value)} className="input w-full" />
              </div>
              <div className="flex-1 min-w-[150px]">
                <label className="label text-xs text-gray-500 mb-1">{t.toDate}</label>
                <input type="date" value={rapportDateTo} onChange={(e) => setRapportDateTo(e.target.value)} className="input w-full" />
              </div>
              <div className="flex-[2] min-w-[240px]">
                <label className="label text-xs text-gray-500 mb-1">
                  {lang === 'fr' ? 'Enseignant (rapport sur TOUTES ses classes — classe non requise)' : 'Teacher (all-classes report — no class needed)'}
                </label>
                <select value={reportTeacherId} onChange={(e) => setReportTeacherId(e.target.value)} className="input w-full">
                  <option value="">{lang === 'fr' ? '— Aucun (rapport par classe ci-dessous) —' : '— None (class report below) —'}</option>
                  {teachers.map(tch => <option key={tch.id} value={tch.id}>{tch.first_name} {tch.last_name}</option>)}
                </select>
              </div>
              <button onClick={() => { loadRapport(); if (reportTeacherId) loadTeacherRapport() }} className="btn-primary">
                <BarChart3 className="w-4 h-4 mr-2" />{t.rapportGenerate}
              </button>
            </div>

            {/* Téléchargements PDF / Excel — par enseignant ET par classe */}
            <div className="flex flex-wrap items-center gap-2 mb-6 pb-4 border-b border-gray-100">
              <span className="text-xs text-gray-500 mr-1">{lang === 'fr' ? 'Télécharger' : 'Download'}:</span>
              <button disabled={!reportTeacherId} onClick={() => handleDownloadTeacherReport('pdf')}
                className="flex items-center gap-1 px-3 py-1.5 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 text-xs border border-red-200 disabled:opacity-40 disabled:cursor-not-allowed">
                <FileDown className="w-3 h-3" /> PDF · {lang === 'fr' ? 'Enseignant' : 'Teacher'}
              </button>
              <button disabled={!reportTeacherId} onClick={() => handleDownloadTeacherReport('excel')}
                className="flex items-center gap-1 px-3 py-1.5 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 text-xs border border-green-200 disabled:opacity-40 disabled:cursor-not-allowed">
                <Table2 className="w-3 h-3" /> Excel · {lang === 'fr' ? 'Enseignant' : 'Teacher'}
              </button>
              <span className="text-gray-300">|</span>
              <button disabled={!selectedClass} onClick={() => handleDownloadClassReport('pdf')}
                className="flex items-center gap-1 px-3 py-1.5 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 text-xs border border-red-200 disabled:opacity-40 disabled:cursor-not-allowed">
                <FileDown className="w-3 h-3" /> PDF · {lang === 'fr' ? 'Classe' : 'Class'}
              </button>
              <button disabled={!selectedClass} onClick={() => handleDownloadClassReport('excel')}
                className="flex items-center gap-1 px-3 py-1.5 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 text-xs border border-green-200 disabled:opacity-40 disabled:cursor-not-allowed">
                <Table2 className="w-3 h-3" /> Excel · {lang === 'fr' ? 'Classe' : 'Class'}
              </button>
            </div>

            {reportTeacherId ? (
              /* ═══ RAPPORT PAR ENSEIGNANT : ventilation par classe ═══ */
              rapportLoading ? (
                <div className="text-center py-12 text-gray-400"><RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />{t.loading}</div>
              ) : !teacherRapport ? (
                <div className="text-center py-12 text-gray-400"><BarChart3 className="w-8 h-8 mx-auto mb-2 opacity-30" />{t.rapportNoData}</div>
              ) : (
                <div>
                  <div className="mb-3 text-sm text-gray-600 space-y-0.5">
                    <div>
                      <strong className="text-gray-900">{teacherRapport.teacher_name}</strong>
                      {teacherRapport.teacher_phone && (
                        <span className="ml-2 text-gray-500">Tél: {teacherRapport.teacher_phone}</span>
                      )}
                      {(rapportDateFrom || rapportDateTo) && (
                        <span className="ml-2">{t.rapportPeriod}: {rapportDateFrom || '—'} → {rapportDateTo || '—'}</span>
                      )}
                    </div>
                    {teacherRapport.teacher_subjects?.length > 0 && (
                      <div className="text-xs text-gray-500">
                        {lang === 'fr' ? 'Matières enseignées' : 'Subjects taught'}: {teacherRapport.teacher_subjects.join(', ')}
                      </div>
                    )}
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200 bg-gray-50/50">
                          <th className="text-left py-3 px-4 font-medium text-gray-600">{lang === 'fr' ? 'Classe' : 'Class'}</th>
                          <th className="text-center py-3 px-4 font-medium text-gray-600">{t.rapportPresent}</th>
                          <th className="text-center py-3 px-4 font-medium text-gray-600">{t.rapportAbsent}</th>
                          <th className="text-center py-3 px-4 font-medium text-gray-600">{t.rapportLate}</th>
                          <th className="text-center py-3 px-4 font-medium text-gray-600">{t.rapportPermission}</th>
                          <th className="text-center py-3 px-4 font-medium text-gray-600">{t.rapportConge}</th>
                          <th className="text-center py-3 px-4 font-medium text-gray-600">{t.rapportDelayTotal}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {teacherRapport.by_class?.length === 0 ? (
                          <tr><td colSpan={7} className="py-8 text-center text-gray-400">{t.rapportNoData}</td></tr>
                        ) : (
                          <>
                            {teacherRapport.by_class?.map((row: any) => (
                              <tr key={row.class_name} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                                <td className="py-3 px-4 font-medium text-gray-900">{row.class_name}</td>
                                <td className="py-3 px-4 text-center text-green-600 font-medium">{row.present_count}</td>
                                <td className="py-3 px-4 text-center text-red-600 font-medium">{row.absent_count}</td>
                                <td className="py-3 px-4 text-center text-amber-600 font-medium">{row.late_count}</td>
                                <td className="py-3 px-4 text-center text-blue-600 font-medium">{row.permission_count}</td>
                                <td className="py-3 px-4 text-center text-purple-600 font-medium">{row.conge_count}</td>
                                <td className="py-3 px-4 text-center text-gray-600">{row.total_delay_minutes} min</td>
                              </tr>
                            ))}
                            {/* Ligne TOTAUX */}
                            <tr className="border-b border-gray-200 bg-gray-50 font-semibold">
                              <td className="py-3 px-4 text-gray-900">{lang === 'fr' ? 'TOTAUX' : 'TOTALS'}</td>
                              <td className="py-3 px-4 text-center text-green-700">{teacherRapport.totals?.present_count ?? 0}</td>
                              <td className="py-3 px-4 text-center text-red-700">{teacherRapport.totals?.absent_count ?? 0}</td>
                              <td className="py-3 px-4 text-center text-amber-700">{teacherRapport.totals?.late_count ?? 0}</td>
                              <td className="py-3 px-4 text-center text-blue-700">{teacherRapport.totals?.permission_count ?? 0}</td>
                              <td className="py-3 px-4 text-center text-purple-700">{teacherRapport.totals?.conge_count ?? 0}</td>
                              <td className="py-3 px-4 text-center text-gray-800">{teacherRapport.totals?.total_delay_minutes ?? 0} min</td>
                            </tr>
                          </>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )
            ) : (
              /* ═══ RAPPORT PAR CLASSE ═══ */
              rapportLoading ? (
                <div className="text-center py-12 text-gray-400"><RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />{t.loading}</div>
              ) : !selectedClass ? (
                <div className="text-center py-12 text-gray-400"><GraduationCap className="w-8 h-8 mx-auto mb-2 opacity-30" />{lang === 'fr' ? 'Sélectionnez une classe (ou un enseignant ci-dessus)' : 'Select a class (or a teacher above)'}</div>
              ) : rapportData.length === 0 ? (
                <div className="text-center py-12 text-gray-400"><BarChart3 className="w-8 h-8 mx-auto mb-2 opacity-30" />{t.rapportNoData}</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200 bg-gray-50/50">
                        <th className="text-left py-3 px-4 font-medium text-gray-600">{t.rapportTeacher}</th>
                        <th className="text-center py-3 px-4 font-medium text-gray-600">{t.rapportPresent}</th>
                        <th className="text-center py-3 px-4 font-medium text-gray-600">{t.rapportAbsent}</th>
                        <th className="text-center py-3 px-4 font-medium text-gray-600">{t.rapportLate}</th>
                        <th className="text-center py-3 px-4 font-medium text-gray-600">{t.rapportPermission}</th>
                        <th className="text-center py-3 px-4 font-medium text-gray-600">{t.rapportConge}</th>
                        <th className="text-center py-3 px-4 font-medium text-gray-600">{t.rapportDelayTotal}</th>
                        <th className="text-center py-3 px-4 font-medium text-gray-600">{t.presenceRate}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rapportData.map((row) => {
                        const total = row.total_records || 1
                        const rate = Math.round((row.present_count / total) * 100)
                        // ✅ Le matricule ne s'affiche QUE s'il ne ressemble pas à un téléphone
                        const isPhone = row.teacher_matricule && /^[+\d][\d\s.\-]{8,}$/.test(row.teacher_matricule)
                        return (
                          <tr key={row.teacher_id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                            <td className="py-3 px-4">
                              <p className="font-medium text-gray-900">{row.teacher_name}</p>
                              {!isPhone && row.teacher_matricule && (
                                <p className="text-xs text-gray-500">{row.teacher_matricule}</p>
                              )}
                            </td>
                            <td className="py-3 px-4 text-center text-green-600 font-medium">{row.present_count}</td>
                            <td className="py-3 px-4 text-center text-red-600 font-medium">{row.absent_count}</td>
                            <td className="py-3 px-4 text-center text-amber-600 font-medium">{row.late_count}</td>
                            <td className="py-3 px-4 text-center text-blue-600 font-medium">{row.permission_count}</td>
                            <td className="py-3 px-4 text-center text-purple-600 font-medium">{row.conge_count}</td>
                            <td className="py-3 px-4 text-center text-gray-600">{row.total_delay_minutes} min</td>
                            <td className="py-3 px-4 text-center">
                              <div className="flex items-center justify-center gap-2">
                                <div className="w-12 h-2 bg-gray-200 rounded-full overflow-hidden">
                                  <div className={`h-full rounded-full ${rate >= 90 ? 'bg-green-500' : rate >= 70 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${rate}%` }} />
                                </div>
                                <span className="text-xs font-medium">{rate}%</span>
                              </div>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )
            )}
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════
          MODAL POINTAGE (libre ou édition EDT)
          ═══════════════════════════════════════════════════════════════ */}
      {showModal && canWrite && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto animate-bounce-in">
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingRecord ? (lang === 'fr' ? 'Modifier le pointage' : 'Edit attendance') : t.newEntry}
              </h3>
              <button onClick={() => setShowModal(false)} className="p-1.5 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5 text-gray-500" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="label">{t.teacher} *</label>
                <select value={formData.teacher_id || ''} onChange={(e) => setFormData({ ...formData, teacher_id: parseInt(e.target.value) })} className="input w-full">
                  <option value="">{lang === 'fr' ? 'Sélectionner...' : 'Select...'}</option>
                  {teachers.map(tch => <option key={tch.id} value={tch.id}>{tch.first_name} {tch.last_name}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">{t.date} *</label>
                  <input type="date" value={formData.attendance_date || ''} onChange={(e) => setFormData({ ...formData, attendance_date: e.target.value })} className="input w-full" />
                </div>
                <div>
                  <label className="label">{t.status} *</label>
                  <select value={formData.status || 'present'} onChange={(e) => setFormData({ ...formData, status: e.target.value as any })} className="input w-full">
                    {STATUS_CONFIG.map(s => <option key={s.value} value={s.value}>{s.label[lang]}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">{t.arrival}</label>
                  <input type="time" value={formData.arrival_time || ''} onChange={(e) => setFormData({ ...formData, arrival_time: e.target.value })} className="input w-full" />
                </div>
                <div>
                  <label className="label">{t.departure}</label>
                  <input type="time" value={formData.departure_time || ''} onChange={(e) => setFormData({ ...formData, departure_time: e.target.value })} className="input w-full" />
                </div>
              </div>
              {formData.status === 'late' && (
                <div>
                  <label className="label">{t.delay}</label>
                  <input type="number" min={0} value={formData.delay_minutes || 0} onChange={(e) => setFormData({ ...formData, delay_minutes: parseInt(e.target.value) })} className="input w-full" />
                </div>
              )}
              <div>
                <label className="label">{t.reason}</label>
                <input type="text" value={formData.reason || ''} onChange={(e) => setFormData({ ...formData, reason: e.target.value })} className="input w-full" placeholder={lang === 'fr' ? 'Motif...' : 'Reason...'} />
              </div>
              <div>
                <label className="label">{t.notes}</label>
                <textarea value={formData.notes || ''} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} className="input w-full min-h-[80px]" placeholder={lang === 'fr' ? 'Observations...' : 'Notes...'} />
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                <button onClick={() => setShowModal(false)} className="btn-outline">{t.cancel}</button>
                <button onClick={handleSave} className="btn-primary">{editingRecord ? t.save : (lang === 'fr' ? 'Créer' : 'Create')}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}